/**
 * Form Section Component
 *
 * Wraps a group of related form fields in a styled container with:
 * - Gray background (colorFillQuaternary)
 * - Optional section title
 * - Consistent padding and spacing
 *
 * Replaces the repetitive fieldContainerStyle usage.
 *
 * Usage:
 * <FormSection title="Basic Information">
 *   <ControlledInput ... />
 *   <ControlledSelect ... />
 * </FormSection>
 */

import React from 'react';
import { Typography } from 'antd';
import { useWizardStyles } from '@/styles/wizardStyles';

const { Text } = Typography;

export interface FormSectionProps {
  /** Optional section title displayed at the top */
  title?: string;

  /** Form fields to display in this section */
  children: React.ReactNode;

  /** Additional class name for the container */
  className?: string;

  /** Additional inline styles (use sparingly) */
  style?: React.CSSProperties;
}

/**
 * Form Section Component
 *
 * Groups related form fields together with consistent styling.
 */
export const FormSection: React.FC<FormSectionProps> = ({
  title,
  children,
  className,
  style,
}) => {
  const styles = useWizardStyles();

  return (
    <div style={{ ...styles.fieldContainer, ...style }} className={className}>
      {title && <Text strong style={styles.sectionTitle}>{title}</Text>}
      {children}
    </div>
  );
};

export default FormSection;
